import { useState, type FormEvent } from "react";
import { X, User, Bell, Lock, Shield, LogOut } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { useNavigate } from "react-router-dom";
import "@/components/ui/Modal.css";

type Tab = "account" | "notifications" | "privacy" | "security";

export function SettingsModal({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<Tab>("account");
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState(user?.displayName || "");
  const [status, setStatus] = useState("online");

  function handleLogout() {
    logout();
    localStorage.removeItem("refreshToken");
    navigate("/login");
    onClose();
  }

  const tabs: { id: Tab; label: string; icon: typeof User }[] = [
    { id: "account", label: "Account", icon: User },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "privacy", label: "Privacy", icon: Lock },
    { id: "security", label: "Security", icon: Shield },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h2 className="modal__title">settings</h2>
          <button className="modal__close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="modal__body">
          <div className="modal__tabs">
            {tabs.map((t) => (
              <button
                key={t.id}
                className={`modal__tab${tab === t.id ? " modal__tab--active" : ""}`}
                onClick={() => setTab(t.id)}
              >
                <t.icon size={16} />
                <span>{t.label}</span>
              </button>
            ))}
          </div>

          <div className="modal__content">
            {tab === "account" && (
              <div className="settings-panel">
                <div className="settings-panel__section">
                  <label className="settings-panel__label">display name</label>
                  <input
                    className="settings-panel__input"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                  />
                </div>
                <div className="settings-panel__section">
                  <label className="settings-panel__label">username</label>
                  <input
                    className="settings-panel__input"
                    value={user?.username || ""}
                    disabled
                  />
                  <span className="settings-panel__hint">usernames cannot be changed</span>
                </div>
                <div className="settings-panel__section">
                  <label className="settings-panel__label">status</label>
                  <select
                    className="settings-panel__select"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="online">online</option>
                    <option value="idle">idle</option>
                    <option value="dnd">do not disturb</option>
                    <option value="offline">invisible</option>
                  </select>
                </div>
                <button className="settings-panel__save">save changes</button>
              </div>
            )}

            {tab === "notifications" && (
              <div className="settings-panel">
                <div className="settings-panel__toggle-row">
                  <span>desktop notifications</span>
                  <ToggleSwitch defaultChecked />
                </div>
                <div className="settings-panel__toggle-row">
                  <span>message sounds</span>
                  <ToggleSwitch defaultChecked />
                </div>
                <div className="settings-panel__toggle-row">
                  <span>call ringtone</span>
                  <ToggleSwitch />
                </div>
                <div className="settings-panel__toggle-row">
                  <span>suppress @everyone</span>
                  <ToggleSwitch />
                </div>
              </div>
            )}

            {tab === "privacy" && (
              <div className="settings-panel">
                <div className="settings-panel__toggle-row">
                  <span>allow DMs from server members</span>
                  <ToggleSwitch defaultChecked />
                </div>
                <div className="settings-panel__toggle-row">
                  <span>show current activity</span>
                  <ToggleSwitch defaultChecked />
                </div>
                <div className="settings-panel__toggle-row">
                  <span>show online status</span>
                  <ToggleSwitch defaultChecked />
                </div>
                <p className="settings-panel__note">
                  all messages are end-to-end encrypted by default. no server-side data collection.
                </p>
              </div>
            )}

            {tab === "security" && (
              <div className="settings-panel">
                <div className="settings-panel__section">
                  <label className="settings-panel__label">change password</label>
                  <input className="settings-panel__input" type="password" placeholder="current password" />
                  <input className="settings-panel__input" type="password" placeholder="new password" style={{ marginTop: 8 }} />
                </div>
                <div className="settings-panel__section">
                  <label className="settings-panel__label">encryption</label>
                  <p className="settings-panel__note">
                    your private key is stored locally, encrypted with your password. the server never has access to it.
                  </p>
                  <button className="settings-panel__outline-btn">export key backup</button>
                </div>
                <div className="settings-panel__danger-zone">
                  <span className="settings-panel__danger-label">danger zone</span>
                  <button className="settings-panel__danger-btn" onClick={handleLogout}>
                    <LogOut size={14} />
                    log out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ToggleSwitch({ defaultChecked = false }: { defaultChecked?: boolean }) {
  const [on, setOn] = useState(defaultChecked);
  return (
    <button
      className={`toggle-switch${on ? " toggle-switch--on" : ""}`}
      onClick={() => setOn(!on)}
      aria-label="Toggle"
    >
      <div className="toggle-switch__knob" />
    </button>
  );
}
